import {
  ScaffolderInputHeader,
  ScaffolderFormPaper,
  ScaffolderFormHeader,
  ScaffolderFieldError,
} from '@internal/plugin-scaffolder-common-field-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import useAsync from 'react-use/lib/useAsync';

import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import {
  Box,
  CircularProgress,
  TextField,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { OWNER_INFO } from '../infoDoc';
import { useStyles } from '../styles';

interface OwnerSectionProps {
  value: string;
  onChange: (value: string) => void;
  errors?: string[];
}

const OwnerSectionInner = ({
  value,
  onChange,
  errors = [],
}: OwnerSectionProps) => {
  const catalogApi = useApi(catalogApiRef);
  const classes = useStyles();

  const ownerError = errors.length > 0 && !value ? 'Owner is required' : '';
  const hasOwnerError = ownerError !== '';

  const ownerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasOwnerError) {
      ownerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [hasOwnerError]);

  const { value: ownerEntities, loading: ownersLoading } = useAsync(() =>
    catalogApi.getEntities({
      filter: { kind: 'Group', 'spec.type': 'supervisory-org' },
    }),
  );

  const ownerOptions: Entity[] = useMemo(
    () => ownerEntities?.items || [],
    [ownerEntities],
  );

  const selectedOwner = useMemo(
    () => ownerOptions.find(e => stringifyEntityRef(e) === value) || null,
    [ownerOptions, value],
  );

  const handleOwnerChange = useCallback(
    (_: any, entity: Entity | null) => {
      onChange(entity ? stringifyEntityRef(entity) : '');
    },
    [onChange],
  );

  return (
    <Box>
      <ScaffolderFormPaper>
        <ScaffolderFormHeader
          title="Owner"
          description="Configure the ownership details for this Deployment as a Service Application."
          divider
        />
        <div ref={ownerRef} className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Application owner"
            description="Select the owner of this application."
            infoDoc={OWNER_INFO}
          />
          <Autocomplete
            size="small"
            value={selectedOwner}
            loading={ownersLoading}
            onChange={handleOwnerChange}
            options={ownerOptions}
            getOptionLabel={option =>
              (option.metadata.title || option.metadata.name) as string
            }
            renderInput={params => (
              <TextField
                {...params}
                label="Owner"
                variant="outlined"
                color="secondary"
                required
                size="small"
                error={hasOwnerError}
                helperText={
                  hasOwnerError ? (
                    <ScaffolderFieldError message={ownerError} />
                  ) : undefined
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {ownersLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={option => (
              <Box>
                <Typography variant="body2" noWrap>
                  {option.metadata.title || option.metadata.name}
                </Typography>
                <Typography variant="caption" noWrap color="textSecondary">
                  {stringifyEntityRef(option)}
                </Typography>
              </Box>
            )}
            noOptionsText="No owners found"
            loadingText="Loading owners..."
          />
        </div>
      </ScaffolderFormPaper>
    </Box>
  );
};

export const OwnerSection = React.memo(OwnerSectionInner);
